import { getPocketBase, escFilter as esc } from '../pocketbase.js';
import { DEFAULT_STUDIO_LABEL_COLOR, normalizeLabelColor } from '../../main/studioLabelColors.js';

/** Normalizza nome etichetta (trim + max 24). */
export function normalizeLabelName(name) {
  return String(name || '').trim().slice(0, 24);
}

/** Sostituisce `from` con `to` in un array labels, dedupe. */
export function replaceLabelInList(labels, from, to) {
  const list = Array.isArray(labels) ? labels : [];
  const out = [];
  const seen = new Set();
  for (const l of list) {
    const next = l === from ? to : l;
    if (!next || seen.has(next)) continue;
    seen.add(next);
    out.push(next);
  }
  return out;
}

/** Rimuove `name` da un array labels. */
export function removeLabelFromList(labels, name) {
  return (Array.isArray(labels) ? labels : []).filter((l) => l !== name);
}

export async function fetchUserLabelCatalog(accessKey, pbOverride) {
  const pb = pbOverride || getPocketBase();
  const rows = await pb.collection('studio_user_labels').getFullList({
    filter: `access_key="${esc(accessKey)}"`,
    sort: 'name',
  });
  return rows
    .map((r) => ({
      id: r.id,
      name: normalizeLabelName(r.name),
      color: normalizeLabelColor(r.color),
    }))
    .filter((r) => r.name)
    .sort((a, b) => a.name.localeCompare(b.name, 'it'));
}

export async function fetchUserLabels(accessKey, pbOverride) {
  const catalog = await fetchUserLabelCatalog(accessKey, pbOverride);
  return catalog.map((r) => r.name);
}

async function findUserLabelRow(pb, accessKey, name) {
  const normalized = normalizeLabelName(name);
  if (!normalized) return null;
  const rows = await pb.collection('studio_user_labels').getFullList({
    filter: `access_key="${esc(accessKey)}"`,
  });
  return rows.find((r) => String(r.name).toLowerCase() === normalized.toLowerCase()) || null;
}

export async function createUserLabel(accessKey, name, color, pbOverride) {
  const pb = pbOverride || getPocketBase();
  const normalized = normalizeLabelName(name);
  if (!normalized) throw new Error('Nome etichetta vuoto');
  const colorExplicit = color !== undefined && color !== null && String(color).trim() !== '';
  const labelColor = colorExplicit ? normalizeLabelColor(color) : null;

  const dup = await findUserLabelRow(pb, accessKey, normalized);
  if (dup) {
    const dupColor = normalizeLabelColor(dup.color);
    if (colorExplicit && labelColor !== dupColor) {
      await pb.collection('studio_user_labels').update(dup.id, { color: labelColor });
      return {
        id: dup.id,
        access_key: accessKey,
        name: dup.name,
        color: labelColor,
        created: false,
      };
    }
    return {
      id: dup.id,
      access_key: accessKey,
      name: dup.name,
      color: dupColor,
      created: false,
    };
  }

  const finalColor = labelColor || DEFAULT_STUDIO_LABEL_COLOR;
  const rec = await pb.collection('studio_user_labels').create({
    access_key: accessKey,
    name: normalized,
    color: finalColor,
  });
  return { ...rec, color: finalColor, created: true };
}

export async function updateUserLabelColor(accessKey, name, color, pbOverride) {
  const pb = pbOverride || getPocketBase();
  const normalized = normalizeLabelName(name);
  if (!normalized) throw new Error('Nome etichetta non valido');
  const labelColor = normalizeLabelColor(color);

  const row = await findUserLabelRow(pb, accessKey, normalized);
  if (!row) {
    const created = await createUserLabel(accessKey, normalized, labelColor, pb);
    return {
      name: created.name || normalized,
      color: created.color || labelColor,
      created: Boolean(created.created),
    };
  }

  await pb.collection('studio_user_labels').update(row.id, { color: labelColor });
  return { name: row.name, color: labelColor, created: false };
}

async function fetchTargetsForAccessKey(pb, accessKey) {
  return pb.collection('studio_targets').getFullList({
    filter: `access_key="${esc(accessKey)}"`,
  });
}

export async function renameUserLabel(accessKey, oldName, newName, pbOverride) {
  const pb = pbOverride || getPocketBase();
  const from = normalizeLabelName(oldName);
  const to = normalizeLabelName(newName);
  if (!from || !to) throw new Error('Nome etichetta non valido');
  if (from === to) return { renamed: 0, targetsUpdated: 0 };

  const labelRows = await pb.collection('studio_user_labels').getFullList({
    filter: `access_key="${esc(accessKey)}"`,
  });
  const row = labelRows.find((r) => r.name === from);
  const clash = labelRows.find(
    (r) => r.name !== from && String(r.name).toLowerCase() === to.toLowerCase(),
  );
  if (clash) throw new Error('Etichetta già esistente');

  const catalogUpdate = row
    ? pb.collection('studio_user_labels').update(row.id, { name: to })
    : Promise.resolve();

  const targets = await fetchTargetsForAccessKey(pb, accessKey);
  const targetUpdates = [];
  for (const t of targets) {
    const labels = Array.isArray(t.labels) ? t.labels : [];
    if (!labels.includes(from)) continue;
    const next = replaceLabelInList(labels, from, to);
    targetUpdates.push(pb.collection('studio_targets').update(t.id, { labels: next }));
  }

  await Promise.all([catalogUpdate, ...targetUpdates]);

  return { renamed: row ? 1 : 0, targetsUpdated: targetUpdates.length, from, to };
}

export async function deleteUserLabel(accessKey, name, pbOverride) {
  const pb = pbOverride || getPocketBase();
  const normalized = normalizeLabelName(name);
  if (!normalized) throw new Error('Nome etichetta non valido');

  const row = await findUserLabelRow(pb, accessKey, normalized);
  const catalogDeletes = row
    ? [pb.collection('studio_user_labels').delete(row.id)]
    : [];

  const targets = await fetchTargetsForAccessKey(pb, accessKey);
  const targetUpdates = [];
  for (const t of targets) {
    const labels = Array.isArray(t.labels) ? t.labels : [];
    if (!labels.includes(normalized)) continue;
    const next = removeLabelFromList(labels, normalized);
    targetUpdates.push(pb.collection('studio_targets').update(t.id, { labels: next }));
  }

  await Promise.all([...catalogDeletes, ...targetUpdates]);

  return { deleted: catalogDeletes.length, targetsUpdated: targetUpdates.length, name: normalized };
}
